import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, get } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';
import { Dialog } from './rundown-dialogs.js';
import { realtime, type WsMessage } from '../ws.js';

export interface FeedSourceDto {
  id: string;
  name: string;
  url: string;
  groupName: string;
  language: string;
  active: boolean;
  intervalMinutes: number;
  browserAgent: boolean;
  lastFetchedAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  lastItemAt: string | null;
  itemCount: number;
}
export interface FeedItemDto {
  id: string;
  sourceId: string;
  guid: string;
  title: string;
  link: string | null;
  summary: string;
  author: string | null;
  imageUrl: string | null;
  publishedAt: string;
  fetchedAt: string;
  /** The inbox rows say whether the article text is stored; the reading pane fetches it. */
  hasBody?: boolean;
  sourceName: string;
  groupName: string;
  language: string;
}

/** A feed picture that hides itself when the address does not load (hot-link protection, a dead resize service). */
export function FeedThumb({ src, className, onError }: { src: string; className: string; onError?: () => void }) {
  const [bad, setBad] = useState(false);
  if (bad) return null;
  return (
    <img
      className={className}
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        setBad(true);
        onError?.();
      }}
    />
  );
}

const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (m < 1) return t('justNow');
  if (m < 60) return `${m}′`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h ${m % 60}′`;
  return fmtDateTime(iso);
};

/** The reading pane of an item: the picture, the text (the whole article when the feed or its page gives it) and the ways out of it. */
export function FeedItemDialog({ item, canCreate, onClose }: { item: FeedItemDto; canCreate: boolean; onClose: () => void }) {
  const [article, setArticle] = useState<{ id: string; body: string | null } | null>(null);
  useEffect(() => {
    let on = true;
    get<{ body: string | null }>(`/api/feeds/items/${item.id}/article`)
      .then((r) => {
        if (on) setArticle({ id: item.id, body: r.body });
      })
      .catch(() => {
        if (on) setArticle({ id: item.id, body: null });
      });
    return () => {
      on = false;
    };
  }, [item.id]);
  const loaded = article && article.id === item.id ? article : null;
  const paragraphs = loaded?.body ? loaded.body.split(/\n\n+/) : null;
  const copyText = () => void navigator.clipboard?.writeText(`${item.title}\n\n${loaded?.body ?? item.summary}\n\n${item.sourceName}${item.link ? ` — ${item.link}` : ''}`);
  return (
    <Dialog title={`${item.sourceName} · ${ago(item.publishedAt)}`} onClose={onClose} width={780}>
      <article className="feed-reader">
        <h2 className="feed-reader-title">{item.title}</h2>
        <div className="feed-reader-meta">
          <span className="status info">{item.sourceName}</span> {item.groupName && <span className="muted">{item.groupName} · </span>}
          <span className="muted" title={fmtDateTime(item.publishedAt)}>
            {fmtDateTime(item.publishedAt)}
          </span>
          {item.author && <span className="muted"> · {item.author}</span>}
        </div>
        {item.imageUrl && <FeedThumb className="feed-reader-img" src={item.imageUrl} />}
        <div className="feed-reader-text">
          {paragraphs ? paragraphs.map((p, i) => <p key={i}>{p}</p>) : <p>{item.summary || '—'}</p>}
          {!loaded && <p className="muted small">{t('feedReading')}</p>}
          {loaded && !loaded.body && item.link && <p className="muted small">{t('feedNoFullText')}</p>}
        </div>
        <div className="toolbar left">
          {canCreate && (
            <Link className="btn primary" to={`/story/new?feedItem=${item.id}`}>
              ✚ {t('feedsNewStory')}
            </Link>
          )}
          {item.link && (
            <a className="btn" href={item.link} target="_blank" rel="noreferrer noopener">
              ↗ {t('feedsOpenSource')}
            </a>
          )}
          <button className="btn" type="button" onClick={copyText}>
            {t('copy')}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            {t('close')}
          </button>
        </div>
      </article>
    </Dialog>
  );
}

/** Ροές: the incoming wires (RSS / Atom) the administrator subscribed to, newest first, with "new story from this". */
export function FeedsPage() {
  const { can } = useSession();
  const [reader, setReader] = useState<FeedItemDto | null>(null);
  const [sources, setSources] = useState<FeedSourceDto[]>([]);
  const [items, setItems] = useState<FeedItemDto[]>([]);
  const [more, setMore] = useState(false);
  const [group, setGroup] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // bumped when the server announces new items, so the list reloads at once
  const [bump, setBump] = useState(0);
  useEffect(() => realtime.listen((m: WsMessage) => m.type === 'feeds' && setBump((n) => n + 1)), []);

  useEffect(() => {
    get<{ sources: FeedSourceDto[] }>('/api/feeds/sources')
      .then((r) => setSources(r.sources))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const query = useCallback(
    (before?: string) => {
      const p = new URLSearchParams();
      if (source) p.set('source', source);
      else if (group) p.set('group', group);
      if (q.trim()) p.set('q', q.trim());
      if (before) p.set('before', before);
      p.set('limit', '60');
      return get<{ items: FeedItemDto[]; more: boolean }>(`/api/feeds/items?${p.toString()}`);
    },
    [source, group, q],
  );

  // first page, again every minute, and at once when the server announces new items
  useEffect(() => {
    let cancelled = false;
    const run = () =>
      query()
        .then((r) => {
          if (cancelled) return;
          setItems(r.items);
          setMore(r.more);
          setError(null);
        })
        .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : String(e)));
    const first = window.setTimeout(run, 150);
    const h = window.setInterval(run, 60_000);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(h);
    };
  }, [query, bump]);

  const loadMore = async () => {
    const last = items[items.length - 1];
    if (!last) return;
    setBusy(true);
    try {
      const r = await query(last.publishedAt);
      setItems((xs) => [...xs, ...r.items]);
      setMore(r.more);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    const m = new Map<string, FeedSourceDto[]>();
    for (const s of sources) {
      const k = s.groupName || t('feedsOther');
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    return [...m.entries()];
  }, [sources]);

  return (
    <>
      <PageTitle title={t('feedsTitle')} hint={t('feedsHint')} />
      {error && <div className="banner err">{error}</div>}
      <div className="feeds-layout">
        <aside className="feeds-side">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} className="feeds-search" />
          <button type="button" className={`feeds-chip${!group && !source ? ' on' : ''}`} onClick={() => (setGroup(''), setSource(''))}>
            {t('feedsAll')} <span className="muted">{sources.reduce((a, s) => a + s.itemCount, 0)}</span>
          </button>
          {groups.map(([g, list]) => (
            <div key={g} className="feeds-group">
              <button type="button" className={`feeds-chip${group === g && !source ? ' on' : ''}`} onClick={() => (setGroup(g), setSource(''))}>
                {g} <span className="muted">{list.reduce((a, s) => a + s.itemCount, 0)}</span>
              </button>
              {list.map((s) => (
                <button key={s.id} type="button" className={`feeds-chip sub${source === s.id ? ' on' : ''}`} onClick={() => (setGroup(g), setSource(s.id))} title={s.lastError ?? s.url}>
                  {s.lastStatus === 'error' ? '⚠ ' : ''}
                  {s.name} <span className="muted">{s.itemCount}</span>
                </button>
              ))}
            </div>
          ))}
          {sources.length === 0 && <p className="muted small">{t('feedsNoSources')}</p>}
        </aside>
        <div className="feeds-list">
          {items.length === 0 && <p className="muted">{t('feedsEmpty')}</p>}
          {items.map((it) => {
            const isOpen = open === it.id;
            return (
              <article key={it.id} className={`feed-item${isOpen ? ' open' : ''}`} onClick={() => setOpen(isOpen ? null : it.id)}>
                {it.imageUrl && <FeedThumb className="feed-thumb" src={it.imageUrl} />}
                <div className="feed-body">
                  <div className="feed-meta">
                    <span className="status info">{it.sourceName}</span> <span className="muted">{it.groupName}</span> · <span className="muted" title={fmtDateTime(it.publishedAt)}>{ago(it.publishedAt)}</span>
                    {it.author && <span className="muted"> · {it.author}</span>}
                  </div>
                  <h3 className="feed-title">{it.title}</h3>
                  <p className={`feed-summary${isOpen ? '' : ' clamp'}`}>{it.summary || '—'}</p>
                  {isOpen && (
                    <div className="toolbar left" onClick={(e) => e.stopPropagation()}>
                      <Link className="btn primary" to={`/story/new?feedItem=${it.id}`}>
                        ✚ {t('feedsNewStory')}
                      </Link>
                      {it.link && (
                        <a className="btn" href={it.link} target="_blank" rel="noreferrer noopener">
                          ↗ {t('feedsOpenSource')}
                        </a>
                      )}
                      <button className="btn" type="button" onClick={() => setReader(it)}>
                        📖 {t('feedRead')}
                      </button>
                      <button className="btn" type="button" onClick={() => void navigator.clipboard?.writeText(`${it.title}\n\n${it.summary}\n\n${it.sourceName}${it.link ? ` — ${it.link}` : ''}`)}>
                        {t('copy')}
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
          {more && (
            <div className="toolbar">
              <button className="btn" type="button" disabled={busy} onClick={() => void loadMore()}>
                {t('feedsMore')}
              </button>
            </div>
          )}
        </div>
      </div>
      {reader && <FeedItemDialog item={reader} canCreate={can('story.create')} onClose={() => setReader(null)} />}
    </>
  );
}
